import React from 'react';
import { Button, ButtonProps, Tooltip } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { UserRole } from '../../types/permissions';

interface PermissionButtonProps extends Omit<ButtonProps, 'onClick'> {
  permission: UserRole;
  onClick?: () => void;
  disabledTooltip?: string;
  children: React.ReactNode;
}

export const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  onClick,
  disabledTooltip,
  children,
  disabled,
  ...buttonProps
}) => {
  const { hasPermission } = usePermissions();
  
  const hasRequiredPermission = hasPermission(permission);
  const isDisabled = disabled || !hasRequiredPermission;
  
  const defaultTooltip = !hasRequiredPermission 
    ? `You don't have permission to perform this action. Required role: ${permission}`
    : undefined;
  
  const tooltipText = disabledTooltip || defaultTooltip;
  
  const button = (
    <Button
      {...buttonProps}
      disabled={isDisabled}
      onClick={hasRequiredPermission ? onClick : undefined}
      sx={{
        ...buttonProps.sx,
        ...(isDisabled && !hasRequiredPermission && {
          opacity: 0.6,
          cursor: 'not-allowed',
        }),
      }}
    >
      {children}
    </Button>
  );
  
  if (tooltipText && isDisabled) {
    return (
      <Tooltip title={tooltipText} arrow>
        <span>{button}</span>
      </Tooltip>
    );
  }
  
  return button;
};
